/**
 * « Modifier » un brouillon d'animation à la main, en attendant le Studio
 * (lot 5) : le document JRAF en JSON, vérifié pendant la frappe par le même
 * validateur que le serveur (erreurs, défauts de mise en page, de rythme et de figure),
 * puis enregistré avec son rapport. Une correction de narration, de
 * position ou de taille ne demande pas de régénérer.
 */

import { useEffect, useMemo, useState } from 'react';
import { Save, Undo2 } from 'lucide-react';
import {
  autoFixJraf,
  isQualityWarning,
  validateJraf,
  type ValidationResult,
} from '@jeraisonne/animation';
import { Button } from '@jeraisonne/ui';
import { AnimationIssues } from '@/features/animations/animation-test-bench';
import { checkedDraft, useSaveAnimationDraft, type AnimationRow } from '@/lib/animations/api';

type Parsed = { ok: true; value: unknown } | { ok: false; message: string };

function parse(text: string): Parsed {
  try {
    return { ok: true, value: JSON.parse(text) as unknown };
  } catch (e) {
    return { ok: false, message: (e as Error).message };
  }
}

function pretty(draft: unknown): string {
  return JSON.stringify(draft, null, 2);
}

/** Valeur stable après `ms` sans changement (vérification pendant la frappe). */
function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function AnimationJsonEditor({
  row,
  toolIds,
}: {
  row: Pick<AnimationRow, 'id' | 'draft' | 'updated_at'>;
  toolIds?: readonly string[];
}) {
  const saved = useMemo(() => pretty(row.draft), [row.draft]);
  const [text, setText] = useState(saved);
  // brouillon relu (régénéré, enregistré ailleurs) : on repart de lui
  useEffect(() => setText(saved), [saved]);
  const save = useSaveAnimationDraft();
  const [done, setDone] = useState(false);

  const checked = useDebounced(text, 400);
  const parsed = useMemo(() => parse(checked), [checked]);
  // erreurs mécaniques (« draw » sur un texte…) : corrigées d'office à l'enregistrement
  const fixes = useMemo(() => (parsed.ok ? autoFixJraf(parsed.value).fixes : []), [parsed]);
  const result: ValidationResult | null = useMemo(
    () =>
      parsed.ok ? validateJraf(autoFixJraf(parsed.value).doc, toolIds ? { toolIds } : {}) : null,
    [parsed, toolIds],
  );
  const dirty = text !== saved;
  const now = parse(text);

  const submit = () => {
    if (!now.ok) return;
    setDone(false);
    save.mutate(
      { id: row.id, ...checkedDraft(now.value, toolIds) },
      { onSuccess: () => setDone(true) },
    );
  };

  const quality = result ? result.warnings.filter(isQualityWarning).length : 0;
  return (
    <div className="space-y-3">
      <label className="block">
        <span className="sr-only">Document JRAF (JSON)</span>
        <textarea
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setDone(false);
          }}
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          rows={18}
          className="border-ink-200 text-ink-900 focus:border-primary-400 w-full rounded-xl border bg-white p-3 font-mono text-[12px] leading-5 outline-none"
        />
      </label>

      <p role="status" className="text-[13px] font-semibold">
        {!parsed.ok ? (
          <span className="text-error-600">JSON illisible : {parsed.message}</span>
        ) : result?.ok ? (
          <span className={quality ? 'text-ink-700' : 'text-success-600'}>
            Valide
            {quality
              ? ` · ${quality} défaut${quality > 1 ? 's' : ''} de mise en page, de rythme ou de figure`
              : ''}
          </span>
        ) : (
          <span className="text-error-600">
            {result?.ok === false ? result.errors.length : 0} erreur
            {result && !result.ok && result.errors.length > 1 ? 's' : ''} : l’animation ne se jouera
            pas
          </span>
        )}
      </p>

      {fixes.length > 0 && (
        <p className="text-ink-600 text-[13px]">
          {fixes.length > 1
            ? `${fixes.length} erreurs simples seront corrigées d’office à l’enregistrement`
            : 'Une erreur simple sera corrigée d’office à l’enregistrement'}{' '}
          ({fixes.map((f) => f.message).join(' ; ')}).
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={submit} disabled={!dirty || !now.ok} loading={save.isPending}>
          <span className="flex items-center gap-2">
            <Save className="h-4 w-4" aria-hidden /> Enregistrer
          </span>
        </Button>
        <Button variant="outline" onClick={() => setText(saved)} disabled={!dirty}>
          <span className="flex items-center gap-2">
            <Undo2 className="h-4 w-4" aria-hidden /> Annuler les modifications
          </span>
        </Button>
        {done && !dirty && (
          <span className="text-success-600 text-[13px] font-semibold">Enregistré.</span>
        )}
      </div>
      {save.error && (
        <p role="alert" className="text-error-600 text-sm">
          {save.error.message}
        </p>
      )}

      {/* le rapport du brouillon enregistré est déjà affiché par la page : ici, celui des modifications */}
      {result && dirty && (
        <AnimationIssues errors={result.ok ? [] : result.errors} warnings={result.warnings} />
      )}
    </div>
  );
}
